import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/api';
import { Eye } from '../home/ui';
import { Card, PageHead, pbtn } from '../account/shared';

// Cambiar contraseña (diseño cuenta.html). Valida en vivo y guarda contra
// `POST /store/auth/change-password`, que exige la contraseña actual: sin eso,
// una sesión abierta en un teléfono prestado alcanzaría para quedarse con la
// cuenta. Las reglas de fuerza son de la pantalla; el servidor solo exige un
// mínimo, así que lo que se ve acá es más estricto, no menos.

const REGLAS = [
    ['len', 'Al menos 8 caracteres', v => v.length >= 8],
    ['up', 'Una letra mayúscula', v => /[A-Z]/.test(v)],
    ['num', 'Un número', v => /[0-9]/.test(v)],
    ['sym', 'Un símbolo (!@#$…)', v => /[^A-Za-z0-9]/.test(v)],
];
const COLORES = ['#ef4444', '#f59e0b', '#0891b2', '#16a34a'];
const NIVELES = ['Débil', 'Regular', 'Buena', 'Muy segura'];

const Campo = ({ id, label, value, onChange, error, children }) => {
    const [ver, setVer] = useState(false);
    return (
        <div className='flex flex-col gap-[7px] mb-4'>
            <label htmlFor={id} className='text-[13px] font-bold text-[#0f172a]'>{label}</label>
            <div className={`flex items-center border-[1.5px] rounded-[12px] bg-white h-12 px-[14px] transition-all focus-within:border-[#0891b2] focus-within:shadow-[0_0_0_4px_rgba(8,145,178,.12)] ${error ? 'border-[#f87171]' : 'border-[#e8e6e1]'}`}>
                <input id={id} type={ver ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} autoComplete='off'
                    className='flex-1 min-w-0 border-none outline-none text-[15px] text-[#0f172a] bg-transparent' />
                <button type='button' onClick={() => setVer(v => !v)} aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'} className='text-[#64748b] grid place-items-center'>
                    <Eye className='w-[18px] h-[18px]' />
                </button>
            </div>
            {children}
        </div>
    );
};

const ChangePassword = () => {
    const [actual, setActual] = useState('');
    const [nueva, setNueva] = useState('');
    const [conf, setConf] = useState('');

    const cumple = REGLAS.map(([, , t]) => t(nueva));
    const nivel = cumple.filter(Boolean).length;
    const coincide = conf && conf === nueva;
    const valido = actual && nivel === 4 && coincide;

    const [guardando, setGuardando] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!valido || guardando) return;
        setGuardando(true);
        try {
            await api.post('/auth/change-password', { actual, nueva });
            toast.success('Contraseña actualizada');
            setActual(''); setNueva(''); setConf('');
        } catch (error) {
            const d = error.response?.data?.detail;
            toast.error(d?.message || 'No pudimos cambiar la contraseña. Intenta de nuevo.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <section>
            <PageHead title='Cambiar contraseña' sub='Usa una contraseña que no uses en otros sitios.' />
            <Card>
                <div className='grid grid-cols-[minmax(0,1fr)_280px] md-lg:grid-cols-1 gap-7 items-start'>
                    <form onSubmit={submit} autoComplete='off'>
                        <Campo id='pw0' label='Contraseña actual' value={actual} onChange={setActual} />
                        <Campo id='pw1' label='Nueva contraseña' value={nueva} onChange={setNueva}>
                            <div className='grid grid-cols-4 gap-[5px] mt-[2px]'>
                                {[0, 1, 2, 3].map(i => (
                                    <i key={i} className='h-[5px] rounded-[5px] transition-colors' style={{ background: nueva && i < nivel ? COLORES[Math.max(nivel - 1, 0)] : '#e8e6e1' }} />
                                ))}
                            </div>
                            <span className='text-[12.5px] text-[#64748b]'>{nueva ? `Seguridad: ${NIVELES[Math.max(nivel - 1, 0)]}` : 'Escribe tu nueva contraseña'}</span>
                        </Campo>
                        <Campo id='pw2' label='Confirmar nueva contraseña' value={conf} onChange={setConf} error={conf && !coincide}>
                            {conf && <span className={`text-[12.5px] ${coincide ? 'text-[#64748b]' : 'text-[#ef4444]'}`}>{coincide ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}</span>}
                        </Campo>
                        <button className={pbtn} disabled={!valido || guardando}>
                            {guardando ? 'Guardando…' : 'Guardar contraseña'}
                        </button>
                    </form>
                    <div className='bg-[#faf9f6] rounded-[16px] p-5'>
                        <b className='block text-[14px] text-[#0f172a] mb-3'>Tu contraseña debe tener</b>
                        <ul className='list-none p-0 m-0 flex flex-col gap-[10px] text-[14px]'>
                            {REGLAS.map(([k, l], i) => (
                                <li key={k} className={`flex gap-[10px] items-center ${cumple[i] ? 'text-[#0f172a]' : 'text-[#64748b]'}`}>
                                    <span className={`w-[18px] h-[18px] rounded-full flex-shrink-0 grid place-items-center border-[1.5px] ${cumple[i] ? 'bg-[#16a34a] border-[#16a34a] text-white' : 'border-[#e8e6e1]'}`}>
                                        {cumple[i] && <svg className='w-[10px] h-[10px]' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3.5' strokeLinecap='round' strokeLinejoin='round'><polyline points='20 6 9 17 4 12' /></svg>}
                                    </span>
                                    {l}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Card>
        </section>
    );
};

export default ChangePassword;
